import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import ClarityOneLayout from "@/components/clarity-one/ClarityOneLayout";
import { clarityOneLoginUrlWithRedirect } from "@shared/clarityOneAuthRedirect";
import { formatClarityOnePhone } from "@/lib/clarityOneFormat";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Film, Loader2 } from "lucide-react";

export default function ClarityOneMyAccountPage() {
  const [, setLocation] = useLocation();
  const utils = trpc.useUtils();

  const { data: me, isLoading } = trpc.clarityOneAuth.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
  });

  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (!isLoading && !me) {
      setLocation(clarityOneLoginUrlWithRedirect("/account"));
    }
  }, [isLoading, me, setLocation]);

  useEffect(() => {
    if (me) {
      setPhone(me.contact ?? "");
      setEmail(me.email ?? "");
    }
  }, [me]);

  const updateProfile = trpc.clarityOneAuth.updateProfile.useMutation({
    onSuccess: (data) => {
      toast.success("Your account details have been saved.");
      void utils.clarityOneAuth.me.invalidate();
      setLocation("/home");
    },
    onError: (err) => toast.error(err.message),
  });

  if (isLoading || !me) {
    return (
      <ClarityOneLayout showNav={false}>
        <div className="flex justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-[#B8956E]" />
        </div>
      </ClarityOneLayout>
    );
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile.mutate({
      contact: phone.trim(),
      email: email.trim() ? email.trim() : null,
    });
  };

  return (
    <ClarityOneLayout showNav>
      <div className="mb-8">
        <Link
          href="/home"
          className="inline-flex items-center gap-1 text-[11px] tracking-[0.15em] uppercase text-[#8B7355] hover:text-[#1A2744] no-underline mb-6"
        >
          <ChevronLeft className="h-4 w-4" />
          Home
        </Link>
        <p className="text-[11px] tracking-[0.2em] uppercase text-[#B8956E] mb-2">My Account</p>
        <h1 className="text-2xl sm:text-3xl font-light text-[#1A2744] tracking-tight">Your details</h1>
        <p className="text-sm text-[#8B7355] mt-2 max-w-lg leading-relaxed">
          Update your contact information. Changes sync to your Clarity customer record.
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="max-w-lg bg-white rounded-2xl border border-[#E8E4DC] p-6 sm:p-8 space-y-6"
      >
        <div className="space-y-2">
          <Label htmlFor="name" className="text-[#1A2744] text-sm">
            Name
          </Label>
          <Input
            id="name"
            value={me.name ?? ""}
            readOnly
            disabled
            className="h-12 bg-[#FAFAF7] border-[#E8E4DC] text-[#8B7355] cursor-not-allowed"
          />
          <p className="text-xs text-[#8B7355]">Your name is managed by Clarity and cannot be changed here.</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone" className="text-[#1A2744] text-sm">
            Phone Number
          </Label>
          <Input
            id="phone"
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="9123 4567"
            className="h-12 border-[#E8E4DC] focus-visible:ring-[#B8956E]"
            autoComplete="tel"
          />
          {phone ? (
            <p className="text-xs text-[#8B7355]">Display: {formatClarityOnePhone(phone)}</p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-[#1A2744] text-sm">
            Email Address
          </Label>
          <Input
            id="email"
            type="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="h-12 border-[#E8E4DC] focus-visible:ring-[#B8956E]"
            autoComplete="email"
          />
        </div>

        <div className="pt-2 flex flex-col sm:flex-row gap-3">
          <Button
            type="submit"
            disabled={updateProfile.isPending}
            className="h-12 flex-1 bg-[#1A2744] hover:bg-[#243556] text-white rounded-xl"
          >
            {updateProfile.isPending ? "Saving…" : "Save"}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-12 flex-1 border-[#E8E4DC] rounded-xl"
            onClick={() => setLocation("/home")}
          >
            Cancel
          </Button>
        </div>
      </form>

      <div className="max-w-lg mt-8 bg-white rounded-2xl border border-[#E8E4DC] p-6 sm:p-8">
        <p className="text-[11px] tracking-[0.2em] uppercase text-[#B8956E] mb-2">Billing</p>
        <h2 className="text-xl font-light text-[#1A2744] tracking-tight">Statement of Account</h2>
        <p className="text-sm text-[#8B7355] mt-2 leading-relaxed">
          Review outstanding balances, due invoices, and payment activity for your accepted
          proposals.
        </p>
        <Button
          asChild
          className="mt-5 h-11 bg-[#1A2744] hover:bg-[#243556] text-white rounded-xl"
        >
          <Link href="/account/statement">View Statement of Account</Link>
        </Button>
      </div>

      {Boolean(me.kaleidescapeWalletEnabled) ? (
        <div className="max-w-lg mt-6 bg-white rounded-2xl border border-[#E8E4DC] p-6 sm:p-8">
          <p className="text-[11px] tracking-[0.2em] uppercase text-[#B8956E] mb-2">Entertainment</p>
          <h2 className="text-xl font-light text-[#1A2744] tracking-tight">Kaleidescape Wallet</h2>
          <p className="text-sm text-[#8B7355] mt-2 leading-relaxed">
            View your wallet balance and top-up history.
          </p>
          <Button
            asChild
            variant="outline"
            className="mt-5 h-11 border-[#E8E4DC] rounded-xl"
          >
            <Link href="/wallet/kaleidescape" className="inline-flex items-center gap-2">
              <Film className="h-4 w-4" strokeWidth={1.5} />
              Open Wallet
              <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </Button>
        </div>
      ) : null}
    </ClarityOneLayout>
  );
}
